<!-- PropertyRegistrationModal.vue -->
<template>
  <div class="modal fade" id="propertyRegistrationModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="mdi mdi-plus-circle me-2"></i>
            {{ isEditing ? 'Edit Property' : 'Register New Property' }}
          </h4>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <form @submit.prevent="submitForm">
            <!-- Basic Information -->
            <div class="card border mb-4">
              <div class="card-header bg-primary text-white">
                <h6 class="mb-0 text-white">
                  <i class="mdi mdi-information me-2"></i>
                  Basic Information
                </h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Property ID <span class="text-danger">*</span></label>
                    <input v-model="form.propertyId" type="text" class="form-control"
                           placeholder="e.g., KJD-MS-001" required>
                    <small class="text-muted">Format: KJD-[TYPE]-[NUMBER]</small>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Property Type <span class="text-danger">*</span></label>
                    <select v-model="form.type" class="form-select" required>
                      <option value="MARKET_STALL">Market Stall</option>
                      <option value="COUNTY_HOUSE">County House</option>
                      <option value="MACHINERY">Machinery</option>
                      <option value="EQUIPMENT">Equipment</option>
                      <option value="VEHICLE">Vehicle</option>
                      <option value="LAND">Land</option>
                    </select>
                  </div>
                  <div class="col-12 mb-3">
                    <label class="form-label">Property Name <span class="text-danger">*</span></label>
                    <input v-model="form.name" type="text" class="form-control"
                           placeholder="e.g., Kajiado Central Market - Stall A1" required>
                  </div>
                  <div class="col-12 mb-3">
                    <label class="form-label">Description</label>
                    <textarea v-model="form.description" class="form-control" rows="2"
                              placeholder="Brief description of the property"></textarea>
                  </div>
                </div>
              </div>
            </div>

            <!-- Location Information -->
            <div class="card border mb-4">
              <div class="card-header bg-info text-white">
                <h6 class="mb-0 text-white">
                  <i class="mdi mdi-map-marker me-2"></i>
                  Location Information
                </h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Sub County <span class="text-danger">*</span></label>
                    <select v-model="form.subCounty" class="form-select" required>
                      <option value="">Select Sub County</option>
                      <option value="Kajiado North">Kajiado North</option>
                      <option value="Kajiado Central">Kajiado Central</option>
                      <option value="Kajiado East">Kajiado East</option>
                      <option value="Kajiado West">Kajiado West</option>
                      <option value="Kajiado South">Kajiado South</option>
                    </select>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Ward <span class="text-danger">*</span></label>
                    <select v-model="form.ward" class="form-select" required>
                      <option value="">Select Ward</option>
                      <option v-for="ward in filteredWards" :key="ward" :value="ward">
                        {{ ward }}
                      </option>
                    </select>
                  </div>
                  <div class="col-12 mb-3">
                    <label class="form-label">Specific Location <span class="text-danger">*</span></label>
                    <input v-model="form.specificLocation" type="text" class="form-control"
                           placeholder="e.g., Kajiado Central Market, Block A" required>
                  </div>
                </div>
              </div>
            </div>

            <!-- Property Specifications -->
            <div class="card border mb-4">
              <div class="card-header bg-success text-white">
                <h6 class="mb-0 text-white">
                  <i class="mdi mdi-ruler me-2"></i>
                  Property Specifications
                </h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Size/Specifications <span class="text-danger">*</span></label>
                    <input v-model="form.size" type="text" class="form-control"
                           :placeholder="getSizePlaceholder()" required>
                    <small class="text-muted">{{ getSizeHint() }}</small>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Condition <span class="text-danger">*</span></label>
                    <select v-model="form.condition" class="form-select" required>
                      <option value="Excellent">Excellent</option>
                      <option value="Good">Good</option>
                      <option value="Fair">Fair</option>
                      <option value="Poor">Poor</option>
                    </select>
                  </div>
                </div>

                <!-- Type-specific fields -->
                <div v-if="form.type === 'MACHINERY' || form.type === 'EQUIPMENT' || form.type === 'VEHICLE'">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Make/Brand</label>
                      <input v-model="form.make" type="text" class="form-control" placeholder="e.g., Caterpillar">
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Model</label>
                      <input v-model="form.model" type="text" class="form-control" placeholder="e.g., 140M">
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Year of Manufacture</label>
                      <input v-model="form.yearOfManufacture" type="number" class="form-control"
                             :min="1980" :max="new Date().getFullYear()">
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Serial Number</label>
                      <input v-model="form.serialNumber" type="text" class="form-control">
                    </div>
                  </div>
                </div>

                <div v-if="form.type === 'VEHICLE'">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Registration Number</label>
                      <input v-model="form.registrationNumber" type="text" class="form-control"
                             placeholder="e.g., KBY 123A">
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Engine Number</label>
                      <input v-model="form.engineNumber" type="text" class="form-control">
                    </div>
                  </div>
                </div>

                <div v-if="form.type === 'COUNTY_HOUSE'">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Number of Bedrooms</label>
                      <select v-model="form.bedrooms" class="form-select">
                        <option value="">Select Bedrooms</option>
                        <option value="1">1 Bedroom</option>
                        <option value="2">2 Bedrooms</option>
                        <option value="3">3 Bedrooms</option>
                        <option value="4">4 Bedrooms</option>
                        <option value="5+">5+ Bedrooms</option>
                      </select>
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Floor Area (m²)</label>
                      <input v-model="form.floorArea" type="number" class="form-control"
                             placeholder="e.g., 200">
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Financial Information -->
            <div class="card border mb-4">
              <div class="card-header bg-warning text-dark">
                <h6 class="mb-0">
                  <i class="mdi mdi-currency-usd me-2"></i>
                  Financial Information
                </h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Monthly Rate (KES) <span class="text-danger">*</span></label>
                    <input v-model="form.monthlyRate" type="number" class="form-control"
                           placeholder="0" required min="0">
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Deposit Required (KES)</label>
                    <input v-model="form.depositRequired" type="number" class="form-control"
                           placeholder="0" min="0">
                  </div>
                  <div class="col-12 mb-3">
                    <div class="form-check">
                      <input v-model="form.isRevenuegenerating" class="form-check-input"
                             type="checkbox" id="revenueGenerating">
                      <label class="form-check-label" for="revenueGenerating">
                        This property generates revenue for the county
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Status Information -->
            <div class="card border">
              <div class="card-header bg-secondary text-white">
                <h6 class="mb-0 text-white">
                  <i class="mdi mdi-check-circle me-2"></i>
                  Status Information
                </h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Initial Status</label>
                    <select v-model="form.status" class="form-select">
                      <option value="AVAILABLE">Available</option>
                      <option value="MAINTENANCE">Under Maintenance</option>
                      <option value="ALLOCATED">Pre-allocated</option>
                    </select>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Insurance Required</label>
                    <select v-model="form.insuranceRequired" class="form-select">
                      <option value="NO">No</option>
                      <option value="YES">Yes</option>
                    </select>
                  </div>
                  <div class="col-12 mb-3">
                    <label class="form-label">Additional Notes</label>
                    <textarea v-model="form.notes" class="form-control" rows="2"
                              placeholder="Any additional information about this property"></textarea>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            Cancel
          </button>
          <button @click="submitForm" type="button" class="btn btn-primary" :disabled="!isFormValid">
            <i class="mdi mdi-content-save me-1"></i>
            {{ isEditing ? 'Update' : 'Register' }} Property
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PropertyRegistrationModal",
  props: {
    property: {
      type: Object,
      default: () => ({})
    },
    isEditing: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: {
        propertyId: '',
        name: '',
        description: '',
        type: 'MARKET_STALL',
        subCounty: '',
        ward: '',
        specificLocation: '',
        size: '',
        condition: 'Good',
        monthlyRate: 0,
        depositRequired: 0,
        status: 'AVAILABLE',
        insuranceRequired: 'NO',
        isRevenuegenerating: true,
        notes: '',
        // Vehicle/Machinery specific
        make: '',
        model: '',
        yearOfManufacture: '',
        serialNumber: '',
        registrationNumber: '',
        engineNumber: '',
        // House specific
        bedrooms: '',
        floorArea: ''
      },

      wardsBySubCounty: {
        'Kajiado North': ['Ongata Rongai', 'Nkaimurunya', 'Olkeri', 'Ngong'],
        'Kajiado Central': ['Kaputiei North', 'Kaputiei Central', 'Ildamat', 'Mashuru'],
        'Kajiado East': ['Kitengela', 'Kaputiei North', 'Oloosirkon/Sholinke'],
        'Kajiado West': ['Keekonyokie', 'Imaroro', 'Purko'],
        'Kajiado South': ['Matapato North', 'Matapato South', 'Kenyawa-Poka', 'Magadi']
      }
    }
  },
  computed: {
    isFormValid() {
      return this.form.propertyId.trim() !== '' &&
          this.form.name.trim() !== '' &&
          this.form.type !== '' &&
          this.form.subCounty !== '' &&
          this.form.ward !== '' &&
          this.form.specificLocation.trim() !== '' &&
          this.form.size.trim() !== '';
    },

    filteredWards() {
      return this.wardsBySubCounty[this.form.subCounty] || [];
    }
  },
  watch: {
    property: {
      handler(newValue) {
        if (newValue && Object.keys(newValue).length > 0) {
          this.form = { ...newValue };
        } else {
          this.resetForm();
        }
      },
      deep: true,
      immediate: true
    },

    'form.subCounty'() {
      // Reset ward when sub county changes
      this.form.ward = '';
    },

    'form.type'() {
      // Update property ID prefix when type changes
      if (!this.isEditing) {
        this.updatePropertyIdPrefix();
      }
    }
  },
  methods: {
    getSizePlaceholder() {
      switch (this.form.type) {
        case 'MARKET_STALL':
          return 'e.g., 4m x 3m';
        case 'COUNTY_HOUSE':
          return 'e.g., 4 Bedrooms, 200m²';
        case 'MACHINERY':
          return 'e.g., Heavy Machinery';
        case 'EQUIPMENT':
          return 'e.g., 10,000 Liters';
        case 'VEHICLE':
          return 'e.g., Double Cabin';
        case 'LAND':
          return 'e.g., 2.5 Acres';
        default:
          return 'Enter size/specifications';
      }
    },

    getSizeHint() {
      switch (this.form.type) {
        case 'MARKET_STALL':
          return 'Enter dimensions (length x width)';
        case 'COUNTY_HOUSE':
          return 'Enter bedrooms and floor area';
        case 'MACHINERY':
          return 'Enter machinery classification';
        case 'EQUIPMENT':
          return 'Enter capacity or specifications';
        case 'VEHICLE':
          return 'Enter vehicle type/classification';
        case 'LAND':
          return 'Enter area in acres or hectares';
        default:
          return '';
      }
    },

    updatePropertyIdPrefix() {
      const prefixes = {
        'MARKET_STALL': 'KJD-MS-',
        'COUNTY_HOUSE': 'KJD-CH-',
        'MACHINERY': 'KJD-MC-',
        'EQUIPMENT': 'KJD-EQ-',
        'VEHICLE': 'KJD-VH-',
        'LAND': 'KJD-LD-'
      };

      if (prefixes[this.form.type]) {
        this.form.propertyId = prefixes[this.form.type] + '001';
      }
    },

    submitForm() {
      if (this.isFormValid) {
        this.$emit('save-property', this.form);
      }
    },

    resetForm() {
      this.form = {
        propertyId: '',
        name: '',
        description: '',
        type: 'MARKET_STALL',
        subCounty: '',
        ward: '',
        specificLocation: '',
        size: '',
        condition: 'Good',
        monthlyRate: 0,
        depositRequired: 0,
        status: 'AVAILABLE',
        insuranceRequired: 'NO',
        isRevenuegenerating: true,
        notes: '',
        make: '',
        model: '',
        yearOfManufacture: '',
        serialNumber: '',
        registrationNumber: '',
        engineNumber: '',
        bedrooms: '',
        floorArea: ''
      };
    }
  }
}
</script>

<style scoped>
.form-label {
  font-weight: 600;
  font-size: 0.875rem;
}

.card-header.bg-primary h6,
.card-header.bg-info h6,
.card-header.bg-success h6,
.card-header.bg-secondary h6 {
  color: white !important;
}

.border {
  border-color: #dee2e6 !important;
}

.form-check-input:checked {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
}
</style>